import type {
  AnyRoute,
  NativeDomain,
  NativeRouteMeta,
} from '@retailer-platform/dashboard/routing-native'
import type { RPPCore } from '@retailer-platform/dashboard/app'
import type { RPPDomain } from '@retailer-platform/dashboard/core'
import { legacyRoutePatterns } from './nativeRoutePatterns'

/**
 * Pure logic for the native domains. Kept free of `import.meta.glob` (see `nativeRoutes.ts`
 * and `nativeRouteMeta.ts`) so it is unit-testable and takes everything discovered as input.
 *
 * Discovery is by file, never by a hand-edited list: a domain's `nativeDomain.ts` (written once)
 * plus, per migrated route, a `<route>.native.meta.ts` (eager, data-only) and a
 * `<route>.native.tsx` (lazy, exports `nativeRoute`). Migrating another route adds files only.
 *
 * Contract: a native boot loads ONLY the domains that serve the boot URL natively. Navigating to
 * another domain's URL falls through to the legacy catch-all (which loads the deferred registry),
 * exactly as it does today — so no catalog gate is needed and the boot cost does not grow with
 * the number of migrated domains.
 */

/** The eager, data-only view of one domain's native routes, grouped from its sidecars. */
export interface NativeDomainRouteMeta {
  routes: readonly string[]
  autoEnable: readonly string[]
  routeFiles: readonly { name: string; base: string }[]
}

/** A domain whose descriptor and native routes have been loaded for the boot URL. */
export interface LoadedNativeDomain {
  domain: NativeDomain
  routes: readonly AnyRoute[]
  /** Route names (keys of `routes.ts`) the domain serves natively, from its sidecars. */
  nativeNames: readonly string[]
}

/** `../../../domains/<dir>/src/routing/<file>` → `<dir>` (the key shared by a domain's files). */
export const nativeDomainKey = (globPath: string): string => {
  const dir = /\/domains\/([^/]+)\//.exec(globPath)?.[1]
  if (!dir) throw new Error(`Cannot derive a domain directory from "${globPath}"`)
  return dir
}

/** Groups glob results by domain directory, ordered by path so the result is stable. */
export const groupByDomain = <T>(entries: Readonly<Record<string, T>>): Record<string, T[]> => {
  const grouped: Record<string, T[]> = {}
  for (const path of Object.keys(entries).sort()) {
    ;(grouped[nativeDomainKey(path)] ??= []).push(entries[path] as T)
  }
  return grouped
}

/** One domain's sidecars → the per-domain metadata the boot decisions read. */
export const groupNativeRouteMeta = (
  sidecars: Readonly<Record<string, NativeRouteMeta>>
): Record<string, NativeDomainRouteMeta> => {
  const grouped: Record<string, { name: string; autoEnable: boolean; base: string }[]> = {}
  const suffix = '.native.meta.ts'
  for (const [path, meta] of Object.entries(sidecars).sort(([a], [b]) => a.localeCompare(b))) {
    const fileName = path.slice(path.lastIndexOf('/') + 1)
    if (!fileName.endsWith(suffix)) throw new Error(`"${path}" does not end with "${suffix}"`)
    ;(grouped[nativeDomainKey(path)] ??= []).push({
      name: meta.name,
      autoEnable: meta.autoEnable,
      base: fileName.slice(0, -suffix.length),
    })
  }
  return Object.fromEntries(
    Object.entries(grouped).map(([dir, entries]) => [
      dir,
      {
        routes: entries.map(entry => entry.name),
        autoEnable: entries.filter(entry => entry.autoEnable).map(entry => entry.name),
        routeFiles: entries.map(({ name, base }) => ({ name, base })),
      },
    ])
  )
}

export const autoEnableRouteNames = (
  metas: Readonly<Record<string, NativeDomainRouteMeta>>
): readonly string[] =>
  Object.entries(metas)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([, meta]) => meta.autoEnable.filter(name => meta.routes.includes(name)))

/** Human-readable problems with the collected metadata; empty when it is consistent. */
export const validateNativeRouteMeta = (
  metas: Readonly<Record<string, NativeDomainRouteMeta>>
): string[] => {
  const problems: string[] = []
  const owners = new Map<string, string>()
  for (const [dir, meta] of Object.entries(metas)) {
    for (const { name, base } of meta.routeFiles) {
      const expectedBase = name
        .replace(/[-_\s]+(.)?/g, (_, char) => (char ? char.toUpperCase() : ''))
        .replace(/^./, first => first.toLowerCase())
      if (base !== expectedBase) {
        problems.push(
          `"${dir}": native meta file "${base}.native.meta.ts" declares route "${name}" (expected file base "${expectedBase}")`
        )
      }
    }
    for (const name of meta.routes) {
      const owner = owners.get(name)
      if (owner !== undefined) {
        problems.push(
          owner === dir
            ? `route "${name}" has more than one native meta file in "${dir}"`
            : `route "${name}" is claimed by both "${owner}" and "${dir}"`
        )
      }
      owners.set(name, dir)
    }
    for (const name of meta.autoEnable) {
      if (!meta.routes.includes(name)) {
        problems.push(`"${dir}" auto-enables "${name}" but does not list it in routes`)
      }
    }
  }
  return problems
}

/** `…/<dir>/src/routing/<base><suffix>` for each path → `{ <dir>: [<base>, …] }` (sorted). */
export const nativeFileBases = (
  globPaths: readonly string[],
  suffix: string
): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {}
  for (const path of [...globPaths].sort()) {
    const base = path.slice(path.lastIndexOf('/') + 1)
    if (!base.endsWith(suffix)) throw new Error(`"${path}" does not end with "${suffix}"`)
    ;(grouped[nativeDomainKey(path)] ??= []).push(base.slice(0, -suffix.length))
  }
  return grouped
}

/**
 * Each route needs BOTH `<base>.native.tsx` (the route) and `<base>.native.meta.ts` (the eager
 * metadata): one without the other would either never boot natively or boot with no route.
 */
export const validateNativeFilePairs = (
  routeBases: Readonly<Record<string, readonly string[]>>,
  metaBases: Readonly<Record<string, readonly string[]>>
): string[] => {
  const problems: string[] = []
  for (const dir of new Set([...Object.keys(routeBases), ...Object.keys(metaBases)])) {
    const routes = routeBases[dir] ?? []
    const metas = metaBases[dir] ?? []
    for (const base of routes.filter(name => !metas.includes(name))) {
      problems.push(`"${dir}": ${base}.native.tsx has no ${base}.native.meta.ts`)
    }
    for (const base of metas.filter(name => !routes.includes(name))) {
      problems.push(`"${dir}": ${base}.native.meta.ts has no ${base}.native.tsx`)
    }
  }
  return problems
}

const PARAM_SEGMENT = /^:[A-Za-z_][A-Za-z0-9_]*$/
const LITERAL_SEGMENT = /^[A-Za-z0-9_.~-]*$/

/**
 * Matches a catalog pattern made only of literal and whole-segment `:param` parts (plus an
 * optional trailing slash). Anything else (`*`, `:id?`, `:id(\d+)`) throws: guessing would
 * select the wrong descriptor for a direct load.
 */
const patternMatchesPath = (pattern: string, pathname: string): boolean => {
  const source = pattern
    .split('/')
    .map(segment => {
      if (PARAM_SEGMENT.test(segment)) return '[^/]+'
      if (LITERAL_SEGMENT.test(segment)) return segment.replace(/\./g, '\\.')
      throw new Error(`Unsupported route pattern syntax "${pattern}" (segment "${segment}")`)
    })
    .join('/')
  return new RegExp(`^${source}/?$`).test(pathname)
}

/**
 * Domain directories that serve `pathname` natively, resolved through the route catalog
 * (name → URL pattern). A route name missing from the catalog fails loudly: skipping it would
 * silently drop that URL to the legacy shell.
 */
export const nativeDomainKeysForPath = (
  metas: Readonly<Record<string, NativeDomainRouteMeta>>,
  catalogRoutes: Readonly<Record<string, string>>,
  pathname: string
): string[] =>
  Object.entries(metas)
    .filter(([dir, meta]) =>
      meta.routes.some(name => {
        const pattern = catalogRoutes[name]
        if (!pattern) {
          throw new Error(
            `Native domain "${dir}" lists route "${name}", which is not in the route catalog`
          )
        }
        return patternMatchesPath(pattern, pathname)
      })
    )
    .map(([dir]) => dir)
    .sort()

export const collectNativeRoutes = (domains: readonly LoadedNativeDomain[]): readonly AnyRoute[] =>
  domains.flatMap(loaded => loaded.routes)

/**
 * The data-only definition a native boot registers for one domain. Its `routes` record IS
 * the totem lookup table (`RPPCore.getTotemInfoByRoute` maps pattern → tool from it) and the
 * route-owner lookup for catalog loading, so it carries every URL the domain owns:
 * the native routes' patterns (derived from the route objects — one source of truth) plus
 * the legacy-only ones — the domain's `routeNames` inventory minus the native names — resolved
 * from the app's route catalog. A name missing from the catalog fails loudly here: dropping it
 * would silently strip that route's totem mapping.
 */
export const buildNativeDomainDefinition = (
  { domain, routes, nativeNames }: LoadedNativeDomain,
  catalogRoutes: Readonly<Record<string, string>>
): RPPDomain<any, any, any> => {
  const legacyOnly = Object.fromEntries(
    domain.routeNames
      .filter(name => !nativeNames.includes(name))
      .map(name => {
        const pattern = catalogRoutes[name]
        if (!pattern) {
          throw new Error(
            `Native domain "${domain.id}" lists routeNames "${name}", which is not in the route catalog`
          )
        }
        return [name, pattern]
      })
  )
  const { EN_US, ...otherLocales } = domain.translations

  return {
    id: domain.id,
    totem: domain.totem,
    routes: { ...legacyRoutePatterns(routes), ...legacyOnly },
    // Every catalog is lazy: the shell loads the visited route's own EN_US (plus the active
    // locale) on entry — at boot for the initial URL. Registering a native domain therefore
    // costs loader closures, not catalogs.
    translations: {},
    lazyTranslations: { EN_US, ...otherLocales },
    routers: { scoped: [] }, // the native page never renders the legacy router
    navigationEntries: [],
    adminNavigationEntries: [],
  }
}

export const registerNativeDomains = (
  app: RPPCore,
  domains: readonly LoadedNativeDomain[]
): void => {
  const catalogRoutes = app.getRoutes()
  for (const loaded of domains) {
    app.registerOnDemandDomain(buildNativeDomainDefinition(loaded, catalogRoutes))
  }
}
